import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useLeague } from '../../features/leagues/api';
import {
  useMatchdays,
  useSeasonMatchdays,
  useCreateMatchday,
} from '../../features/matchdays/api';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { usePageTitle } from '../../lib/usePageTitle';
import { formatDateTimeFull, formatDateOnly, isTimeTBD } from '../../lib/dates';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { Select } from '../../components/Select';
import { LeagueNav } from './LeagueNav';

export const STATE_LABELS: Record<string, string> = {
  DRAFT: 'Bozza',
  SCHEDULED: 'Programmata',
  OPEN: 'Pronostici aperti',
  CLOSED: 'Pronostici chiusi',
  RESULTS_PENDING: 'In attesa dei risultati',
  RESULTS_VERIFIED: 'Risultati verificati',
  SCORING: 'Calcolo in corso',
  PUBLISHED: 'Pubblicata',
};

export function MatchdayListPage() {
  const { id = '' } = useParams();
  const league = useLeague(id);
  usePageTitle(league.data ? `Giornate — ${league.data.name}` : 'Giornate');
  const matchdays = useMatchdays(id);
  const isAdmin = league.data?.myRole === 'ADMIN' || league.data?.myRole === 'SUPER_ADMIN';

  return (
    <AppLayout title={league.data ? `Giornate — ${league.data.name}` : 'Giornate'}>
      <LeagueNav leagueId={id} />

      {matchdays.isLoading && (
        <p role="status" className="mt-6">Caricamento giornate…</p>
      )}

      {matchdays.data && matchdays.data.length === 0 && (
        <p className="mt-6 text-lg">
          Nessuna giornata ancora.{isAdmin && ' Creane una qui sotto per iniziare.'}
        </p>
      )}

      {matchdays.data && matchdays.data.length > 0 && (
        <ul className="mt-6 flex flex-col gap-3">
          {matchdays.data.map((md) => (
            <li key={md.id}>
              <Link
                to={`/leghe/${id}/giornate/${md.id}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ink-200 p-4 no-underline hover:border-panda-600 dark:border-ink-800 dark:hover:border-panda-400"
              >
                <span className="text-lg font-bold text-ink-900 dark:text-ink-50">
                  Giornata {md.number}
                  {md.isSuper && ' ⭐ Super'}
                </span>
                <span className="text-ink-700 dark:text-ink-200">
                  {STATE_LABELS[md.state] ?? md.state}
                  {md.state === 'OPEN' && md.deadline && ` · chiude ${formatDateTimeFull(md.deadline)}`}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {isAdmin && <CreateMatchdaySection leagueId={id} />}
    </AppLayout>
  );
}

function CreateMatchdaySection({ leagueId }: { leagueId: string }) {
  const { announce } = useAnnouncer();
  const league = useLeague(leagueId);
  const seasonMatchdays = useSeasonMatchdays(leagueId, true);
  const createMatchday = useCreateMatchday(leagueId);
  const [selected, setSelected] = useState('');
  const [deadline, setDeadline] = useState('');
  const [error, setError] = useState<string | null>(null);

  const available = seasonMatchdays.data?.filter((m) => !m.alreadyInLeague) ?? [];
  const autoDeadline = league.data?.automation.autoDeadline ?? false;

  // Creabile = la prima partita è ancora abbastanza lontana (il server rifiuta
  // le giornate già iniziate o troppo vicine: meglio dirlo PRIMA, nel select).
  const isCreatable = (m: (typeof available)[number]) =>
    !m.firstKickoffAt || new Date(m.firstKickoffAt).getTime() > Date.now() + 15 * 60 * 1000;

  // Preseleziona la prossima giornata creabile: niente scelte alla cieca.
  useEffect(() => {
    if (selected) return;
    const next = available.find(isCreatable);
    if (next) setSelected(next.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonMatchdays.data, selected]);

  const optionLabel = (m: (typeof available)[number]) => {
    const base = `Giornata ${m.number} (${m.matchCount} partite)`;
    if (!m.firstKickoffAt) return base;
    if (!isCreatable(m)) return `${base} — già iniziata`;
    return isTimeTBD(m.firstKickoffAt)
      ? `${base} — dal ${formatDateOnly(m.firstKickoffAt)}`
      : `${base} — ${formatDateTimeFull(m.firstKickoffAt)}`;
  };

  return (
    <section aria-labelledby="crea-giornata" className="mt-10 rounded-2xl border border-ink-200 p-5 dark:border-ink-800">
      <h2 id="crea-giornata" className="text-xl font-bold tracking-tight">
        Crea una giornata (amministrazione)
      </h2>
      {error && (
        <p role="alert" className="mt-3 font-medium text-danger">{error}</p>
      )}
      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end">
        <div className="flex flex-col gap-1">
          <label htmlFor="sel-giornata" className="font-medium">Giornata del campionato</label>
          <Select
            id="sel-giornata"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">Seleziona…</option>
            {available.map((m) => (
              <option key={m.id} value={m.id} disabled={!isCreatable(m)}>
                {optionLabel(m)}
              </option>
            ))}
          </Select>
        </div>
        {!autoDeadline && (
          <div className="flex flex-col gap-1">
            <label htmlFor="sel-deadline" className="font-medium">Chiusura pronostici</label>
            <input
              id="sel-deadline"
              type="datetime-local"
              className="min-h-11 rounded-lg border border-ink-300 px-3 py-2 dark:border-ink-600 dark:bg-ink-900"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </div>
        )}
        <Button
          onClick={async () => {
            setError(null);
            if (!selected) {
              setError('Seleziona una giornata del campionato.');
              return;
            }
            try {
              await createMatchday.mutateAsync({
                seasonMatchdayId: selected,
                ...(deadline && !autoDeadline ? { deadline: new Date(deadline).toISOString() } : {}),
              });
              announce(
                autoDeadline
                  ? 'Giornata creata: i pronostici si apriranno automaticamente entro cinque minuti e si chiuderanno da soli prima della prima partita.'
                  : 'Giornata creata in stato bozza.',
              );
              setSelected('');
              setDeadline('');
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Errore nella creazione.');
            }
          }}
          loading={createMatchday.isPending}
          loadingText="Creazione…"
        >
          Crea giornata
        </Button>
      </div>
    </section>
  );
}
